/**
 * Iter 137 · Wave C · Knowledge → Licensing workspace.
 *
 * Reads and writes `pureh_ci_licenses` via `/api/pureh/licensing/{venture}`.
 * Single canonical data source shared with Company Setup.
 */
import { useEffect, useMemo, useState } from "react";
import { Search, Plus, Download, RefreshCw, X, Upload, ArrowRight, Archive as ArchIcon, ShieldAlert } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

type License = {
  id: string;
  ventureId: string;
  kind: string;
  licenseNumber: string;
  issuer: string;
  issueDate?: string;
  expiryDate?: string;
  status: string;
  documentRef?: string;
  notes?: string;
  responsibleOwner?: string;
  department?: string;
  renewalLeadDays: number;
  renewalStatus: string;
  attachments: string[];
  daysUntilExpiry?: number | null;
  expiryState: "ok" | "due_soon" | "urgent" | "expired" | "unknown";
  lastRenewedAt?: string;
};

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const r = await fetch(`${API}${path}`, {
    ...init,
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(init?.headers || {}) },
  });
  if (!r.ok) {
    let msg = `HTTP ${r.status}`;
    try { const j = await r.json(); msg = j.detail || msg; } catch { /* noop */ }
    throw new Error(msg);
  }
  return r.json();
}

const VENTURE = "orvan";

export default function Licensing() {
  const [rows, setRows] = useState<License[]>([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [expiry, setExpiry] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [selected, setSelected] = useState<License | null>(null);
  const [creating, setCreating] = useState(false);

  const reload = async () => {
    setBusy(true); setErr(null);
    try { setRows(await api<License[]>(`/api/pureh/licensing/${VENTURE}`)); }
    catch (e: any) { setErr(e.message); }
    finally { setBusy(false); }
  };

  useEffect(() => { void reload(); }, []);

  const filtered = useMemo(() => rows.filter((r) => {
    const ql = q.toLowerCase();
    if (ql && !(r.licenseNumber || "").toLowerCase().includes(ql) && !(r.issuer || "").toLowerCase().includes(ql) && !(r.kind || "").toLowerCase().includes(ql)) return false;
    if (status && r.status !== status) return false;
    if (expiry && r.expiryState !== expiry) return false;
    return true;
  }), [rows, q, status, expiry]);

  const kpis = useMemo(() => {
    const total = rows.length;
    const active = rows.filter((r) => r.status === "active").length;
    const dueSoon = rows.filter((r) => r.expiryState === "due_soon" || r.expiryState === "urgent").length;
    const expired = rows.filter((r) => r.expiryState === "expired").length;
    return { total, active, dueSoon, expired };
  }, [rows]);

  const exportCsv = () => {
    window.open(`${API}/api/pureh/licensing/${VENTURE}/export.csv`, "_blank", "noopener");
  };

  return (
    <div className="space-y-6" data-testid="licensing-page">
      <header className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500">Knowledge</div>
          <h1 className="text-2xl font-semibold text-slate-900">Licensing</h1>
          <p className="text-sm text-slate-600 max-w-2xl mt-1">
            Regulatory licenses across ventures — same records as Company Setup, enriched with renewal reminders and expiry status.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportCsv} data-testid="licensing-export-csv" className="text-xs border border-slate-300 rounded px-3 py-1.5 hover:bg-slate-50 inline-flex items-center gap-1.5"><Download size={12} /> CSV</button>
          <button onClick={() => setCreating(true)} data-testid="licensing-new" className="text-sm bg-slate-900 text-white rounded px-3 py-1.5 hover:bg-slate-800 inline-flex items-center gap-1.5"><Plus size={13} /> New license</button>
        </div>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Total"    value={kpis.total}    testId="kpi-total" />
        <KpiCard label="Active"   value={kpis.active}   testId="kpi-active" tone="green" />
        <KpiCard label="Due soon" value={kpis.dueSoon}  testId="kpi-due-soon" tone="amber" />
        <KpiCard label="Expired"  value={kpis.expired}  testId="kpi-expired"  tone="red" />
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={14} className="absolute left-2 top-2.5 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search license, issuer or type…"
                 data-testid="licensing-search"
                 className="w-full border border-slate-300 rounded pl-7 pr-2 py-1.5 text-sm" />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="licensing-filter-status" className="border border-slate-300 rounded px-2 py-1.5 text-sm">
          <option value="">All statuses</option>
          <option value="active">active</option>
          <option value="suspended">suspended</option>
          <option value="archived">archived</option>
          <option value="expired">expired</option>
        </select>
        <select value={expiry} onChange={(e) => setExpiry(e.target.value)} data-testid="licensing-filter-expiry" className="border border-slate-300 rounded px-2 py-1.5 text-sm">
          <option value="">All expiry states</option>
          <option value="ok">ok</option>
          <option value="due_soon">due soon</option>
          <option value="urgent">urgent (≤30d)</option>
          <option value="expired">expired</option>
        </select>
        <button onClick={reload} disabled={busy} className="text-slate-500 hover:text-slate-800 flex items-center gap-1 text-xs" data-testid="licensing-refresh"><RefreshCw size={12} /> Refresh</button>
      </div>

      {err && <div className="text-sm text-red-600" data-testid="licensing-error">{err}</div>}

      <div className="border border-slate-200 rounded overflow-hidden">
        <table className="w-full text-sm" data-testid="licensing-table">
          <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wide">
            <tr>
              <th className="px-3 py-2 text-left">Type</th>
              <th className="px-3 py-2 text-left">License #</th>
              <th className="px-3 py-2 text-left">Issuer</th>
              <th className="px-3 py-2 text-left">Expiry</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-left">Owner</th>
              <th className="px-3 py-2 text-left">Dept</th>
              <th className="px-3 py-2 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.id} className="border-t border-slate-200 hover:bg-slate-50 cursor-pointer" onClick={() => setSelected(r)} data-testid={`lic-row-${r.id}`}>
                <td className="px-3 py-2 text-slate-700">{r.kind}</td>
                <td className="px-3 py-2 font-medium text-slate-900">{r.licenseNumber}</td>
                <td className="px-3 py-2 text-slate-700">{r.issuer}</td>
                <td className="px-3 py-2"><ExpiryPill state={r.expiryState} days={r.daysUntilExpiry} date={r.expiryDate} /></td>
                <td className="px-3 py-2"><StatusPill status={r.status} /></td>
                <td className="px-3 py-2 text-xs text-slate-600">{r.responsibleOwner || "—"}</td>
                <td className="px-3 py-2 text-xs text-slate-600">{r.department || "—"}</td>
                <td className="px-3 py-2 text-right text-slate-400"><ArrowRight size={13} /></td>
              </tr>
            ))}
            {filtered.length === 0 && !busy && <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-500 text-sm">No licenses match your filters.</td></tr>}
          </tbody>
        </table>
      </div>

      {selected && <EditPanel license={selected} onClose={() => setSelected(null)} onSaved={() => { setSelected(null); void reload(); }} />}
      {creating && <CreatePanel onClose={() => setCreating(false)} onSaved={() => { setCreating(false); void reload(); }} />}
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────

function KpiCard({ label, value, testId, tone }: { label: string; value: number; testId: string; tone?: "green"|"amber"|"red" }) {
  const cls = tone === "green" ? "text-emerald-700" : tone === "amber" ? "text-amber-600" : tone === "red" ? "text-red-600" : "text-slate-900";
  return (
    <div className="border border-slate-200 rounded p-3" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`text-2xl font-semibold ${cls}`}>{value}</div>
    </div>
  );
}

function ExpiryPill({ state, days, date }: { state: string; days?: number | null; date?: string }) {
  const cls = state === "ok" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : state === "due_soon" ? "bg-amber-50 text-amber-700 border-amber-200"
    : state === "urgent" ? "bg-orange-50 text-orange-700 border-orange-200"
    : state === "expired" ? "bg-red-50 text-red-700 border-red-200"
    : "bg-slate-50 text-slate-600 border-slate-200";
  const label = date ? new Date(date).toLocaleDateString() : "—";
  const suffix = days == null ? "" : days < 0 ? ` (${Math.abs(days)}d overdue)` : ` (in ${days}d)`;
  return <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border ${cls}`}>{label}{suffix}</span>;
}

function StatusPill({ status }: { status: string }) {
  const s = (status || "active").toLowerCase();
  const cls = s === "active" ? "bg-emerald-100 text-emerald-700 border-emerald-200"
    : s === "suspended" ? "bg-orange-100 text-orange-700 border-orange-200"
    : s === "archived" ? "bg-slate-100 text-slate-500 border-slate-200"
    : s === "expired" ? "bg-red-100 text-red-700 border-red-200"
    : "bg-slate-100 text-slate-700 border-slate-200";
  return <span className={`inline-block text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border ${cls}`}>{s}</span>;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.14em] text-slate-500 mb-1">{label}</label>
      {children}
    </div>
  );
}

function EditPanel({ license, onClose, onSaved }: { license: License; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState<License>(license);
  const [newAtt, setNewAtt] = useState("");
  const [renewOpen, setRenewOpen] = useState(false);
  const [newExpiry, setNewExpiry] = useState("");
  const [newLic, setNewLic] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    setBusy(true); setErr(null);
    try {
      await api(`/api/pureh/licensing/${license.ventureId}/${license.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          kind: form.kind, licenseNumber: form.licenseNumber, issuer: form.issuer,
          issueDate: form.issueDate, expiryDate: form.expiryDate, status: form.status,
          documentRef: form.documentRef, notes: form.notes,
          responsibleOwner: form.responsibleOwner, department: form.department,
          renewalLeadDays: form.renewalLeadDays, renewalStatus: form.renewalStatus,
          attachments: form.attachments,
        }),
      });
      onSaved();
    } catch (e: any) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const doRenew = async () => {
    if (!newExpiry) { setErr("Enter a new expiry date"); return; }
    setBusy(true); setErr(null);
    try {
      await api(`/api/pureh/licensing/${license.ventureId}/${license.id}/renew`, {
        method: "POST", body: JSON.stringify({ newExpiryDate: newExpiry, newLicenseNumber: newLic || undefined }),
      });
      onSaved();
    } catch (e: any) { setErr(e.message); }
    finally { setBusy(false); }
  };

  const doArchive = async () => {
    if (!confirm("Archive this license?")) return;
    try { await api(`/api/pureh/licensing/${license.ventureId}/${license.id}/archive`, { method: "POST" }); onSaved(); }
    catch (e: any) { setErr(e.message); }
  };

  const doSuspend = async () => {
    if (!confirm("Suspend this license?")) return;
    try { await api(`/api/pureh/licensing/${license.ventureId}/${license.id}/suspend`, { method: "POST" }); onSaved(); }
    catch (e: any) { setErr(e.message); }
  };

  return (
    <div className="fixed inset-0 z-40 flex" data-testid="licensing-edit-panel">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-[520px] bg-white border-l border-slate-200 h-full overflow-y-auto p-5 space-y-3">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500">License</div>
            <div className="text-lg font-semibold text-slate-900">{license.licenseNumber}</div>
          </div>
          <button onClick={onClose} data-testid="lic-close"><X size={16} /></button>
        </div>

        <Field label="Type"><input value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} data-testid="lic-kind" className="w-full border border-slate-300 rounded px-2 py-1 text-sm" /></Field>
        <Field label="License number"><input value={form.licenseNumber} onChange={(e) => setForm({ ...form, licenseNumber: e.target.value })} data-testid="lic-number" className="w-full border border-slate-300 rounded px-2 py-1 text-sm" /></Field>
        <Field label="Issuing authority"><input value={form.issuer} onChange={(e) => setForm({ ...form, issuer: e.target.value })} data-testid="lic-issuer" className="w-full border border-slate-300 rounded px-2 py-1 text-sm" /></Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Issue date"><input type="date" value={(form.issueDate || "").slice(0, 10)} onChange={(e) => setForm({ ...form, issueDate: e.target.value })} data-testid="lic-issue" className="w-full border border-slate-300 rounded px-2 py-1 text-sm" /></Field>
          <Field label="Expiry date"><input type="date" value={(form.expiryDate || "").slice(0, 10)} onChange={(e) => setForm({ ...form, expiryDate: e.target.value })} data-testid="lic-expiry" className="w-full border border-slate-300 rounded px-2 py-1 text-sm" /></Field>
        </div>
        <Field label="Responsible owner"><input value={form.responsibleOwner || ""} onChange={(e) => setForm({ ...form, responsibleOwner: e.target.value })} data-testid="lic-owner" className="w-full border border-slate-300 rounded px-2 py-1 text-sm" /></Field>
        <Field label="Department"><input value={form.department || ""} onChange={(e) => setForm({ ...form, department: e.target.value })} data-testid="lic-dept" className="w-full border border-slate-300 rounded px-2 py-1 text-sm" /></Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Renewal lead (days)"><input type="number" value={form.renewalLeadDays} onChange={(e) => setForm({ ...form, renewalLeadDays: Number(e.target.value) || 60 })} data-testid="lic-lead" className="w-full border border-slate-300 rounded px-2 py-1 text-sm" /></Field>
          <Field label="Renewal status">
            <select value={form.renewalStatus} onChange={(e) => setForm({ ...form, renewalStatus: e.target.value })} data-testid="lic-rstatus" className="w-full border border-slate-300 rounded px-2 py-1 text-sm">
              <option value="not_due">not due</option>
              <option value="pending">pending</option>
              <option value="renewed">renewed</option>
            </select>
          </Field>
        </div>
        <Field label="Attachments">
          <div className="flex flex-wrap gap-1 mb-1" data-testid="lic-attachments">
            {(form.attachments || []).map((a, i) => (
              <span key={i} className="text-[11px] px-2 py-0.5 rounded border border-slate-300 bg-slate-50 text-slate-600 flex items-center gap-1">
                {a}
                <button onClick={() => setForm({ ...form, attachments: form.attachments.filter((_, j) => j !== i) })} className="text-red-500"><X size={10} /></button>
              </span>
            ))}
          </div>
          <div className="flex gap-1">
            <input value={newAtt} onChange={(e) => setNewAtt(e.target.value)} placeholder="Attachment ref / URL" data-testid="lic-new-att" className="flex-1 border border-slate-300 rounded px-2 py-1 text-xs" />
            <button onClick={() => { if (newAtt) { setForm({ ...form, attachments: [...(form.attachments||[]), newAtt] }); setNewAtt(""); } }} data-testid="lic-add-att" className="text-xs border border-slate-300 rounded px-2 py-1 hover:bg-slate-50 inline-flex items-center gap-1"><Upload size={11} /> Add</button>
          </div>
        </Field>
        <Field label="Notes"><textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="lic-notes" rows={3} className="w-full border border-slate-300 rounded px-2 py-1 text-sm" /></Field>

        {err && <div className="text-xs text-red-600" data-testid="lic-error">{err}</div>}

        <div className="flex gap-2 pt-2 border-t border-slate-200">
          <button onClick={save} disabled={busy} data-testid="lic-save" className="bg-slate-900 text-white text-sm rounded px-3 py-1.5 hover:bg-slate-800 disabled:opacity-50">{busy ? "Saving…" : "Save"}</button>
          <button onClick={() => setRenewOpen(!renewOpen)} data-testid="lic-renew-toggle" className="text-slate-700 border border-slate-300 text-sm rounded px-3 py-1.5 hover:bg-slate-50 inline-flex items-center gap-1"><RefreshCw size={13} /> Renew</button>
          <button onClick={doSuspend} data-testid="lic-suspend" className="text-orange-700 border border-orange-300 text-sm rounded px-3 py-1.5 hover:bg-orange-50 inline-flex items-center gap-1"><ShieldAlert size={13} /> Suspend</button>
          <button onClick={doArchive} data-testid="lic-archive" className="text-slate-500 border border-slate-300 text-sm rounded px-3 py-1.5 hover:bg-slate-50 inline-flex items-center gap-1"><ArchIcon size={13} /> Archive</button>
        </div>

        {renewOpen && (
          <div className="border border-slate-200 rounded p-3 space-y-2 bg-slate-50" data-testid="lic-renew-block">
            <div className="text-xs uppercase tracking-wide text-slate-500">Renew license</div>
            <input type="date" value={newExpiry} onChange={(e) => setNewExpiry(e.target.value)} data-testid="lic-renew-expiry" className="w-full border border-slate-300 rounded px-2 py-1 text-sm" />
            <input value={newLic} onChange={(e) => setNewLic(e.target.value)} placeholder="New license number (optional)" data-testid="lic-renew-number" className="w-full border border-slate-300 rounded px-2 py-1 text-sm" />
            <button onClick={doRenew} data-testid="lic-renew-submit" className="bg-slate-900 text-white text-sm rounded px-3 py-1.5">Confirm renewal</button>
          </div>
        )}
      </div>
    </div>
  );
}

function CreatePanel({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState<Partial<License>>({ kind: "", licenseNumber: "", issuer: "", status: "active", renewalLeadDays: 60, renewalStatus: "not_due", attachments: [] });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      await api(`/api/pureh/licensing/${VENTURE}`, { method: "POST", body: JSON.stringify(form) });
      onSaved();
    } catch (e: any) { setErr(e.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-40 flex" data-testid="licensing-create-panel">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-[480px] bg-white border-l border-slate-200 h-full overflow-y-auto p-5 space-y-3">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500">Knowledge · Licensing</div>
            <div className="text-lg font-semibold text-slate-900">New license</div>
          </div>
          <button onClick={onClose} data-testid="new-close"><X size={16} /></button>
        </div>
        <Field label="Type"><input value={form.kind || ""} onChange={(e) => setForm({ ...form, kind: e.target.value })} data-testid="new-kind" className="w-full border border-slate-300 rounded px-2 py-1 text-sm" /></Field>
        <Field label="License number"><input value={form.licenseNumber || ""} onChange={(e) => setForm({ ...form, licenseNumber: e.target.value })} data-testid="new-number" className="w-full border border-slate-300 rounded px-2 py-1 text-sm" /></Field>
        <Field label="Issuing authority"><input value={form.issuer || ""} onChange={(e) => setForm({ ...form, issuer: e.target.value })} data-testid="new-issuer" className="w-full border border-slate-300 rounded px-2 py-1 text-sm" /></Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Issue"><input type="date" value={form.issueDate || ""} onChange={(e) => setForm({ ...form, issueDate: e.target.value })} data-testid="new-issue" className="w-full border border-slate-300 rounded px-2 py-1 text-sm" /></Field>
          <Field label="Expiry"><input type="date" value={form.expiryDate || ""} onChange={(e) => setForm({ ...form, expiryDate: e.target.value })} data-testid="new-expiry" className="w-full border border-slate-300 rounded px-2 py-1 text-sm" /></Field>
        </div>
        <Field label="Responsible owner"><input value={form.responsibleOwner || ""} onChange={(e) => setForm({ ...form, responsibleOwner: e.target.value })} data-testid="new-owner" className="w-full border border-slate-300 rounded px-2 py-1 text-sm" /></Field>
        <Field label="Department"><input value={form.department || ""} onChange={(e) => setForm({ ...form, department: e.target.value })} data-testid="new-dept" className="w-full border border-slate-300 rounded px-2 py-1 text-sm" /></Field>
        {err && <div className="text-xs text-red-600" data-testid="new-error">{err}</div>}
        <div className="flex gap-2 pt-2">
          <button onClick={submit} disabled={busy || !form.kind || !form.licenseNumber || !form.issuer} data-testid="new-submit" className="bg-slate-900 text-white text-sm rounded px-3 py-1.5 disabled:opacity-50">{busy ? "Creating…" : "Create"}</button>
          <button onClick={onClose} className="text-slate-600 text-sm">Cancel</button>
        </div>
      </div>
    </div>
  );
}
